<template>
  <div>
    <input
      type="text"
      v-model="search"
      placeholder="Search..."
      class="form-control search-input"
    />
    <Tree
      :key="treeData.id"
      :value="treeData.tree"
      :foldingTransition="false"
      @node-folded-changed="nodeToggled"
    >
      <template #default="{ node, path }">
        <span class="tree-item" @click="nodeClicked(node, path, $event)">
          <i :class="node.icon"></i>
          {{ node.text }}
        </span>
      </template>
    </Tree>
  </div>
</template>
<script>
import Tree from "@/components/common/tree/Tree.vue";
import documentModelMixin from "@/mixins/documentModel";
import documentModel from "@/services/word/documentModelService";
import contentControls from "@/services/word/contentControlsService";
import { newGuid } from "@/util/guid";
import {
  SECTION,
  TAG,
  REPEATING_CONTENT_BLOCK,
  REPEATING_TABLE_BLOCK,
  CONTENT_BLOCK,
  HTML_CONTENT_BLOCK,
} from "@/constants/contentControlTypes";
import { BUSINESS_RULE } from "@/constants/businessRule";

const DOCUMENT_ID = "__document";
const RULES_ID = "__rules";
const GROUP_ID = "__group";

const getGroupId = function(type) {
  return `${GROUP_ID}_${type}`;
};

const getIcon = function(type) {
  switch (type) {
    case SECTION:
      return "fa-solid fa-puzzle-piece";
    case TAG:
      return "fa-solid fa-tags";
    case REPEATING_CONTENT_BLOCK:
    case REPEATING_TABLE_BLOCK:
      return "fa-solid fa-list-ul";
    case CONTENT_BLOCK:
    case HTML_CONTENT_BLOCK:
      return "fa-solid fa-file-code";
    default:
      return "fa-solid fa-file";
  }
};

const getGroupText = function(type) {
  switch (type) {
    case SECTION:
      return "Sections";
    case TAG:
      return "Tags";
    case REPEATING_CONTENT_BLOCK:
      return "Repeating Sections";
    case REPEATING_TABLE_BLOCK:
      return "Repeating Tables";
    case CONTENT_BLOCK:
      return "Content Blocks";
    case HTML_CONTENT_BLOCK:
      return "HTML Content Blocks";
  }
};

const getText = function(contentControl) {
  const name = contentControl.label || contentControl.systemName;
  if (contentControl.version && !isNaN(contentControl.version.number)) {
    return `${name} (Version: ${contentControl.version.number})`;
  }

  return name;
};

const isContentControl = function(item) {
  return [
    SECTION,
    TAG,
    REPEATING_CONTENT_BLOCK,
    REPEATING_TABLE_BLOCK,
    CONTENT_BLOCK,
    HTML_CONTENT_BLOCK,
  ].some((type) => type === item.type);
};

const applyContentControls = function(
  contentControls,
  businessRules,
  attachPoint
) {
  const contentControlTypes = [
    TAG,
    SECTION,
    CONTENT_BLOCK,
    HTML_CONTENT_BLOCK,
    REPEATING_CONTENT_BLOCK,
    REPEATING_TABLE_BLOCK,
  ];

  for (let h = 0; h < contentControlTypes.length; h++) {
    const contentControlType = contentControlTypes[h];
    const contentControlsOfType = contentControls.filter(
      (c) => c.type === contentControlType
    );

    if (contentControlsOfType.length > 0) {
      const groupId = getGroupId(contentControlType);

      const groupItem = {
        id: groupId,
        text: getGroupText(contentControlType),
        value: groupId,
        icon: getIcon(contentControlType),
        opened: this.isOpen(groupId),
        children: [],
        visible: true, // force group headings to be visible
      };

      for (let i = 0; i < contentControlsOfType.length; i++) {
        const contentControl = contentControlsOfType[i];

        const item = {
          id: contentControl.id,
          type: contentControl.type,
          text: getText(contentControl),
          value: contentControl,
          icon: getIcon(contentControl.type),
          opened: this.isOpen(contentControl.id),
          children: [],
          visible: this.isSearchedFor(getText(contentControl)), // not part of vjstree, just used for filtering
        };

        if (contentControl.displayRuleId) {
          const displayRule = businessRules.find(
            (r) => r.id === contentControl.displayRuleId
          );
          if (displayRule) {
            item.children.push({
              id: displayRule.id,
              type: BUSINESS_RULE,
              text: displayRule.name,
              value: displayRule,
              icon: "fa-solid fa-code-branch",
              opened: this.isOpen(displayRule.id),
              visible: item.visible || this.isSearchedFor(displayRule.name), // not part of vjstree, just used for filtering
            });
          }
        }

        this.applyContentControls(
          contentControl.contentControls,
          businessRules,
          item.children
        );

        item.visible = item.visible || item.children.some((r) => this.isSearchedFor(r.text));

        if (item.visible) {
          groupItem.children.push(item);
        }
      }

      groupItem.visible = groupItem.visible || groupItem.children.some((r) => this.isSearchedFor(r.text));

      if (groupItem.visible) {
        attachPoint.push(groupItem);
      }
    }
  }
};

const nodeClicked = function(node, path, e) {
  if (
    [DOCUMENT_ID, RULES_ID, GROUP_ID].every((id) => typeof node.id === "number" || !node.id.startsWith(id))
  ) {
    if (isContentControl(node)) {
      contentControls.select(node.id);
    } else {
      contentControls.deselect();
    }

    this.$emit("item-select", { type: node.type, item: node.value });
  } else {
    contentControls.deselect();
    this.$emit("item-select", null);
  }
};

const nodeToggled = function(node) {
  if (!node.$folded) {
    // Node is now expanded
    if (!this.openNodes.includes(node.id)) {
      this.openNodes.push(node.id);
    }
  } else {
    // Node is now collapsed
    const idx = this.openNodes.indexOf(node.id);
    if (idx !== -1) {
      this.openNodes.splice(idx, 1);
    }
  }
};

const isOpen = function(id) {
  return this.openNodes.indexOf(id) !== -1;
};

/**
 * @param {string} text The text to search in.
 * @returns {boolean} True if `text` contains `this.search`, or if `this.search` is blank.
 */
const isSearchedFor = function(text) {
  return (
    !Boolean(this.search?.trim()) ||
    text.toLowerCase().includes(this.search.trim().toLowerCase())
  );
}

export default {
  components: { Tree },
  mixins: [documentModelMixin],
  data: function() {
    return {
      openNodes: [],
      search: "",
    };
  },
  computed: {
    treeData: function() {
      this.logDebug("Generating tree");

      const model = this.documentModel || documentModel.create();
      const tree = [];

      const document = {
        id: DOCUMENT_ID,
        text: "Document",
        icon: "fa-solid fa-file",
        value: "document",
        opened: this.isOpen(DOCUMENT_ID),
        children: [],
      };

      this.applyContentControls(
        model.contentControls,
        model.businessRules,
        document.children
      );

      const rules = {
        id: RULES_ID,
        text: "Display Rules",
        icon: "fa-solid fa-code-branch",
        value: "rules",
        opened: this.isOpen(RULES_ID),
        children: [],
        visible: true, // force group headings to be visible
      };

      for (let i = 0; i < model.businessRules.length; i++) {
        const displayRule = model.businessRules[i];

        const rule = {
          id: displayRule.id,
          type: BUSINESS_RULE,
          text: displayRule.name,
          value: displayRule,
          icon: "fa-solid fa-code-branch",
          opened: this.isOpen(displayRule.id),
          visible: this.isSearchedFor(displayRule.name), // not part of vjstree, just used for filtering
        };

        if (rule.visible) {
            rules.children.push(rule);
        }
      }

      rules.visible = rules.visible || rules.children.some((r) => this.isSearchedFor(r.text));

      if (rules.visible) {
        document.children.push(rules);
      }

      tree.push(document);

      const data =  {
        id: newGuid(),
        tree,
      };

      this.logDebug("Generated tree.", data);
      return data;
    },
  },
  methods: {
    applyContentControls,
    nodeClicked,
    nodeToggled,
    isOpen,
    isSearchedFor,
  },
  emits: ["item-select"],
};
</script>
<style lang="scss">
@import "@/_css/variables.scss";

.search-input {
  margin: 8px 8px 12px 8px;
  width: calc(100% - 16px);
}

.tree-item {
  cursor: pointer;
  display: inline-block;
  padding: 4px 8px;
  border-radius: $border-radius;
  transition: background-color 0.15s ease-in-out;

  &:hover {
    background-color: $gray-200;
  }

  i {
    margin-right: 6px;
    color: $brand-700;
  }
}
</style>
